import { AppFlow, ErrorCode } from '@lib/web-shared';
import { PolicyHelper, ClubHelper } from '@ace-clublabs/payments-helper';
import { BrokerRegion, ClubCodes } from '@ace-clublabs/payment-types';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sources } from '../api/sources';
import SubmitInsuranceForm from '../components/SubmitInsuranceForm';
import {
  allowedNonACEClubs,
  autoPayPath,
  guidewireClubs,
  maintenanceEndDate,
  maintenanceStartDate,
} from '../shared/constants';
import { Pages } from '../shared/pages';
import { useGlobalState } from '../shared/state';
import { ErrorType, IGetPolicyPaymentInfoRequest } from '../shared/types';
import CookieHelper from '../utils/cookieHelper';
import { getNonAceClubCodeFromPath, isAllowedNonACEClub } from '../utils/methods';
import { UtagHelper } from '../utils/utagHelper';
import { setBotChallengeRetryResolver, clearBotChallengeRetryResolver } from '../utils/botDetectionHelper';
import Spinner from '../components/Spinner';

const SubmitInsurancePage: React.FC = () => {
  const navigate = useNavigate();
  const [_appFlow, setAppFlow] = useGlobalState('appFlow');
  const [brokerRegion, setBrokerRegion] = useGlobalState('brokerRegion');
  const [, setPolicyPaymentInfo] = useGlobalState('policyPaymentInfo');
  const [, setPolicyNumber] = useGlobalState('policyNumber');
  const [, setAmountChosen] = useGlobalState('amountChosen');
  const [_clubCode, setClubCode] = useGlobalState('clubCode');
  const [startPath, setStartPath] = useGlobalState('startPath');
  const [, setClubTag] = useGlobalState('clubTag');
  const [, setPolicyMetaData] = useGlobalState('policyMetaData');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [, setInMaintenance] = useGlobalState('inMaintenance');
  const [_policyNumber] = useGlobalState('policyNumber');

  const [isLoading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [notificationError, setNotificationError] = useState('');
  const [tagging, setIsTagging] = useState(true);
  const [tags, setTags] = useState({} as any);

  const [zipClubCode, setZipClubCode] = useState(CookieHelper.getClubCodeFromZipcodeCookie());
  const updateClubCodeFromCookie = () => setZipClubCode(CookieHelper.getClubCodeFromZipcodeCookie());

  useEffect(() => {
    if (!startPath) return;

    const startDate = new Date(maintenanceStartDate).toISOString();
    const endDate = new Date(maintenanceEndDate).toISOString();
    const currentDate = new Date().toISOString();

    const isInMaintenanceWindow = currentDate >= startDate && currentDate <= endDate;
    const guidewireClub = guidewireClubs.includes(startPath);
    if (isInMaintenanceWindow && guidewireClub) {
      setInMaintenance(true);
      navigate(Pages.Maintenance, { startDate, endDate } as any);
    }
  }, [startPath]);

  useEffect(() => {
    const { pathname } = window?.location;
    const appFlow = pathname.startsWith(autoPayPath) ? AppFlow.AUTOPAY : AppFlow.PAYMENT;
    setAppFlow(appFlow);

    const params = new URLSearchParams(window.location.search);
    const brokerRegion = (params.get('backend') as BrokerRegion)?.toUpperCase() || undefined;
    if (brokerRegion === BrokerRegion.A1 || brokerRegion === BrokerRegion.B1) {
      setBrokerRegion(brokerRegion);
    }

    const clubCode = getNonAceClubCodeFromPath(pathname);
    const allowedNonACEClub = isAllowedNonACEClub(allowedNonACEClubs, pathname);
    if (clubCode && allowedNonACEClub) {
      setStartPath(clubCode);
      setClubCode(clubCode);
    } else {
      document.addEventListener('zipRouteUpdate', updateClubCodeFromCookie);
    }

    const clubTag = ClubHelper.getClubAcronym((clubCode || zipClubCode) as ClubCodes);
    setClubTag(clubTag);

    let pageTags = {
      ...tags,
      ...UtagHelper.loadCommonTags(appFlow),
      page: UtagHelper.getPages(appFlow).submitInsurancePage,
      club: clubTag,
    };

    UtagHelper.tag(pageTags);
    setTags(pageTags);
    setIsTagging(false);

    return () => {
      document.removeEventListener('zipRouteUpdate', updateClubCodeFromCookie);
      clearBotChallengeRetryResolver();
    };
  }, []);

  const getPolicyPaymentInfo = async (obj: any) => {
    let parameters: IGetPolicyPaymentInfoRequest;
    try {
      const policyNumber = (obj['policy-number'] || _policyNumber)?.toUpperCase() || '';
      setLoading(true);
      setPolicyNumber(policyNumber);
      const clubCode = startPath ? startPath : PolicyHelper.getProductInfoFromPolicyNumber(policyNumber).clubCode;
      setClubCode(clubCode as ClubCodes);
      const appFlow = _appFlow;

      const clubTag = ClubHelper.getClubAcronym((clubCode || zipClubCode) as ClubCodes);
      setClubTag(clubTag);

      parameters = {
        policyNumber,
        clubCode,
        appFlow,
        brokerRegion,
      };

      const response = await sources.getPolicyPaymentInfo(parameters);
      const { status, allowAutoPay, minimumDue, fullBalance, isEnrolledInAutoPay, lastName } = response;
      const hasNoBalanceAndNoAutoPay = allowAutoPay === false && minimumDue === 0 && fullBalance === 0;
      const noBalanceDue = hasNoBalanceAndNoAutoPay || status === 206;

      setPolicyPaymentInfo(response);
      setAmountChosen(undefined);
      setLoading(false);

      const ineligibleForAutoPay = appFlow === AppFlow.AUTOPAY && !allowAutoPay;
      const alreadyEnrolledInAutoPay = appFlow === AppFlow.AUTOPAY && isEnrolledInAutoPay;

      let warning = '';
      if (status === 204) {
        warning = 'We are unable to process your request at this time.';
        setNotificationError(warning);
        UtagHelper.link({
          ...tags,
          error: warning.toLowerCase().replace('.', ''),
        });
      } else if (alreadyEnrolledInAutoPay) {
        warning = 'You are already enrolled in Auto Pay.';
        setNotificationError('You are already enrolled in Auto Pay.');
        UtagHelper.link({
          ...tags,
          error: warning.toLowerCase().replace('.', ''),
        });
      } else if (ineligibleForAutoPay) {
        warning = 'Your account is not eligible for Auto Pay. Please call {phoneNumber} for assistance.';
        setFieldError(warning);
        UtagHelper.link({
          ...tags,
          error: warning.toLowerCase().split('.')[0],
        });
      } else if (status === 208) {
        warning = 'There is already a payment in processing.';
        setFieldError(warning);
        UtagHelper.link({
          ...tags,
          error: warning.toLowerCase().replace('.', ''),
        });
      } else if (noBalanceDue) {
        warning = 'There is no balance due at this time.';
        setNotificationError(warning);
        UtagHelper.link({
          ...tags,
          error: warning.toLowerCase().replace('.', ''),
        });
      } else if (!lastName) {
        warning = 'No last name on file.';
        setNotificationError(warning);
        UtagHelper.link({
          ...tags,
          error: warning.toLowerCase().replace('.', ''),
        });
      } else {
        setPolicyMetaData({
          ...PolicyHelper.getProductInfoFromPolicyNumber(policyNumber),
          prefix: policyNumber.substring(0, 3),
        });
        navigate(Pages.VerifyPolicy);
      }
    } catch (error: unknown) {
      const err = error as {
        type?: string;
        code?: number;
        error?: { response?: { status?: number; data?: { pxEvent?: boolean } } };
      };
      const isBotDetectionResponse =
        err?.type === ErrorType.BOT_ERROR ||
        (err?.error?.response?.status === 403 && !!err?.error?.response?.data?.pxEvent);
      if (isBotDetectionResponse) {
        UtagHelper.fireBotTag('security check');
        setLoading(false);
        const retryParams = parameters!;
        setBotChallengeRetryResolver((isValid) => {
          if (isValid) {
            getPolicyPaymentInfo(retryParams);
          }
        });
      } else {
        UtagHelper.clearBotDetectionStatus();
        if (err?.type === ErrorType.SOFT_ERROR) {
          if (err?.code === ErrorCode.POLICY_CANCELLED) {
            setNotificationError('Your policy has expired or been canceled.');
            setLoading(false);
          } else {
            setNotificationError('We are unable to process your request at this time.');
            setLoading(false);
          }
        } else {
          navigate(Pages.SorryPage, {
            state: { errorTag: UtagHelper.Errors.systemError, errorType: ErrorType.SYSTEM_ERROR },
          });
        }
      }
    }
  };

  if (tagging) {
    return <></>;
  }

  return isLoading ? (
    <Spinner />
  ) : (
    <SubmitInsuranceForm
      onSubmit={getPolicyPaymentInfo}
      clubCode={_clubCode || zipClubCode}
      fieldError={fieldError}
      notificationError={notificationError}
      isMobileApp={isMobileApp}
    />
  );
};

export default SubmitInsurancePage;
